import {
	EvaluatePartConfiguration,
	PartConfigurationEvaluation,
	PartConfigurationPropertyEvaluation,
	PartConfigurationSchema,
	PartConfigurationSchemaProperty,
	PartConfigurationSchemaValidationIssue,
	PartConfigurationSubConfigurationValue,
	PartConfigurationTreePathElement,
	PartConfigurationValue,
} from "src/components/views/erp/configurator/configuratorForm/propertyModel/partConfigurationSdk.ts";

/**
 * Input values of one configuration by property name, as the configuration schema takes them; a sub-configuration is
 * given as its child's values and a list as an array of them. An absent input gets its default on every evaluation.
 */
export type PropertyInputValues = Record<string, PartConfigurationValue>;

/** One step from a configuration to its child: the child's property and its index (0 for a sub-configuration). */
export interface PropertyModelNodePathStep {
	propertyName: string;
	index: number;
}

/** The path from the root configuration; empty for the root. */
export type PropertyModelNodePath = PropertyModelNodePathStep[];

/** One configuration of an evaluated tree. */
export interface PropertyModelNode {
	path: PropertyModelNodePath;
	/** The key of the child's model in the schema's childModels; undefined for the root. */
	modelKey: string | undefined;
	/** The model key of the node's parent; undefined for the root and its children. */
	parentModelKey?: string;
	properties: PartConfigurationPropertyEvaluation[];
}

export function propertyModelNodePathKey(path: PropertyModelNodePath): string {
	return path.map(({ propertyName, index }) => `${propertyName}[${index}]`).join("/");
}

export function isSubConfigurationInput(value: unknown): value is PartConfigurationSubConfigurationValue {
	return typeof value === "object" && value != null && !Array.isArray(value) && "valuesByPropertyName" in value;
}

/** The children given for a sub-configuration property, as a list whether it is a list or a single child. */
function getChildInputs(value: unknown): PartConfigurationSubConfigurationValue[] {
	if (Array.isArray(value)) return value.filter(isSubConfigurationInput);
	return isSubConfigurationInput(value) ? [value] : [];
}

export function getNodeInputValues(
	rootValues: PropertyInputValues,
	path: PropertyModelNodePath,
): PropertyInputValues | undefined {
	let values: PropertyInputValues | undefined = rootValues;
	for (const { propertyName, index } of path) {
		if (values == null) return undefined;
		values = getChildInputs(values[propertyName])[index]?.valuesByPropertyName;
	}
	return values;
}

/** Replaces the values of the configuration at [path] with the result of [update]. */
export function updateNodeInputValues(
	rootValues: PropertyInputValues,
	path: PropertyModelNodePath,
	update: (values: PropertyInputValues) => PropertyInputValues,
): PropertyInputValues {
	const [first, ...rest] = path;
	if (first == null) return update(rootValues);

	const { propertyName, index } = first;
	const current = rootValues[propertyName];
	const updateChild = (child: PartConfigurationSubConfigurationValue): PartConfigurationSubConfigurationValue => ({
		...child,
		valuesByPropertyName: updateNodeInputValues(child.valuesByPropertyName, rest, update),
	});

	if (Array.isArray(current)) {
		return {
			...rootValues,
			[propertyName]: current.map((child, childIndex) => (childIndex === index ? updateChild(child) : child)),
		};
	}
	if (isSubConfigurationInput(current) && index === 0) {
		return { ...rootValues, [propertyName]: updateChild(current) };
	}
	return rootValues;
}

/** Sets an input of the configuration at [path]; undefined removes it, so that it gets its default. */
export function setNodeInputValue(
	rootValues: PropertyInputValues,
	path: PropertyModelNodePath,
	propertyName: string,
	value: PartConfigurationValue | undefined,
): PropertyInputValues {
	return updateNodeInputValues(rootValues, path, (values) => {
		if (value === undefined) {
			const { [propertyName]: _removed, ...rest } = values;
			return rest;
		}
		return { ...values, [propertyName]: value };
	});
}

/** Adds a child to property [propertyName] of the configuration at [parentPath] and returns its path. */
export function addChildNode({
	rootValues,
	parentPath,
	propertyName,
	isList,
	child = { valuesByPropertyName: {} },
}: {
	rootValues: PropertyInputValues;
	parentPath: PropertyModelNodePath;
	propertyName: string;
	isList: boolean;
	child?: PartConfigurationSubConfigurationValue;
}): { rootValues: PropertyInputValues; childPath: PropertyModelNodePath } {
	const existingChildren = getChildInputs(getNodeInputValues(rootValues, parentPath)?.[propertyName]);
	const index = isList ? existingChildren.length : 0;
	return {
		rootValues: updateNodeInputValues(rootValues, parentPath, (values) => ({
			...values,
			[propertyName]: isList ? [...existingChildren, child] : child,
		})),
		childPath: [...parentPath, { propertyName, index }],
	};
}

/** Removes the child at [childPath]; a sub-configuration becomes empty and the later list elements move up. */
export function removeChildNode(
	rootValues: PropertyInputValues,
	childPath: PropertyModelNodePath,
): PropertyInputValues {
	const parentPath = childPath.slice(0, -1);
	const step = childPath[childPath.length - 1];
	if (step == null) return rootValues;

	return updateNodeInputValues(rootValues, parentPath, (values) => {
		const current = values[step.propertyName];
		if (Array.isArray(current)) {
			return { ...values, [step.propertyName]: current.filter((_, index) => index !== step.index) };
		}
		const { [step.propertyName]: _removed, ...rest } = values;
		return rest;
	});
}

/** Adds a copy of the list element at [childPath] to the end of its list and returns the copy's path. */
export function cloneChildNode(
	rootValues: PropertyInputValues,
	childPath: PropertyModelNodePath,
): { rootValues: PropertyInputValues; childPath: PropertyModelNodePath } | undefined {
	const parentPath = childPath.slice(0, -1);
	const step = childPath[childPath.length - 1];
	if (step == null) return undefined;

	const siblings = getNodeInputValues(rootValues, parentPath)?.[step.propertyName];
	if (!Array.isArray(siblings)) return undefined;

	const source = siblings[step.index];
	if (source == null) return undefined;

	return addChildNode({
		rootValues,
		parentPath,
		propertyName: step.propertyName,
		isList: true,
		child: structuredClone(source),
	});
}

export function getRootNode(evaluation: PartConfigurationEvaluation): PropertyModelNode {
	return { path: [], modelKey: undefined, properties: evaluation.properties };
}

/** The evaluated children of [node] by property, each at its position in the input. */
export function getChildNodes(
	node: PropertyModelNode,
): { propertyName: string; isList: boolean; nodes: PropertyModelNode[] }[] {
	return node.properties.flatMap((property) => {
		const children = property.children ?? [];
		if (children.length === 0) return [];
		return [
			{
				propertyName: property.name,
				isList: children.some((child) => child.index != null),
				nodes: children.map((child) => ({
					path: [...node.path, { propertyName: property.name, index: child.index ?? 0 }],
					modelKey: child.modelKey,
					parentModelKey: node.modelKey,
					properties: child.properties,
				})),
			},
		];
	});
}

export function getNode(
	evaluation: PartConfigurationEvaluation,
	path: PropertyModelNodePath,
): PropertyModelNode | undefined {
	let node: PropertyModelNode | undefined = getRootNode(evaluation);
	for (const step of path) {
		node = getChildNodes(node)
			.find(({ propertyName }) => propertyName === step.propertyName)
			?.nodes.find((child) => child.path[child.path.length - 1]?.index === step.index);
		if (node == null) return undefined;
	}
	return node;
}

/** The properties of [node]'s model in [schema], in layout order. */
export function getNodeSchemaProperties(
	schema: PartConfigurationSchema,
	node: PropertyModelNode,
): PartConfigurationSchemaProperty[] {
	if (node.modelKey == null) return schema.properties;
	return schema.childModels[node.modelKey]?.properties ?? [];
}

/** The part number of [node]'s model in [schema]. */
export function getNodePartNo(schema: PartConfigurationSchema, node: PropertyModelNode): string | undefined {
	if (node.modelKey == null) return schema.context.partNo;
	return schema.childModels[node.modelKey]?.context.partNo;
}

function isSamePath(issuePath: PartConfigurationTreePathElement[], path: PropertyModelNodePath): boolean {
	return (
		issuePath.length === path.length &&
		issuePath.every(
			(element, index) =>
				element.property === path[index]?.propertyName && (element.index ?? 0) === path[index]?.index,
		)
	);
}

/** The issues of the configuration at [path], without those of its children. */
export function getNodeIssues(
	evaluation: PartConfigurationEvaluation,
	path: PropertyModelNodePath,
): PartConfigurationSchemaValidationIssue[] {
	return evaluation.issues.filter((issue) => isSamePath(issue.path, path));
}

/**
 * Evaluates [values] with the schema's rules and removes the given inputs that the form has no field for, so that
 * they do not block saving: inputs of properties that are not applicable or that the part does not use, and values
 * that differ from the value a rule fixes. A removed input gets its default again when it becomes applicable or
 * editable. Inputs are only removed, so evaluating again ends.
 */
export function evaluatePropertyModelInput(
	evaluatePartConfiguration: EvaluatePartConfiguration,
	schema: PartConfigurationSchema,
	values: PropertyInputValues,
): { values: PropertyInputValues; evaluation: PartConfigurationEvaluation } {
	let currentValues = values;
	for (;;) {
		const evaluation = evaluatePartConfiguration(schema, currentValues);
		const valuesWithFields = removeInputsWithoutField(currentValues, evaluation.properties);
		if (JSON.stringify(valuesWithFields) === JSON.stringify(currentValues)) {
			return { values: currentValues, evaluation };
		}
		currentValues = valuesWithFields;
	}
}

/** The inputs that one edit set: inputs of the configuration at [path], or a sub-configuration property there. */
export interface PropertyModelEdit {
	path: PropertyModelNodePath;
	propertyNames: string[];
	/** Positions of the children that the edit removed from the list of its only property, in the old list. */
	removedChildIndexes?: number[];
}

/**
 * After [edit], removes the given values of inputs whose default the edit changed, so that they follow their defaults
 * again: a preset re-applies the values it fills, and an input keeps a value that the user set while its default stays
 * the same. A removed input changes the defaults that read it, so this repeats until no default changes. The inputs
 * that the edit set keep their values, and so do inputs of configurations that [previousEvaluation] did not have, such
 * as a copied child.
 */
export function resetInputsWithChangedDefaults(
	evaluatePartConfiguration: EvaluatePartConfiguration,
	schema: PartConfigurationSchema,
	previousEvaluation: PartConfigurationEvaluation,
	values: PropertyInputValues,
	edit: PropertyModelEdit,
): PropertyInputValues {
	const editedKeys = new Set(
		edit.propertyNames.map((propertyName) => `${propertyModelNodePathKey(edit.path)}#${propertyName}`),
	);
	let before = withoutRemovedChildren(previousEvaluation.properties, edit, []);
	let currentValues = values;
	for (;;) {
		const after = evaluatePartConfiguration(schema, currentValues).properties;
		const resetValues = removeInputsWithChangedDefaults(currentValues, before, after, [], editedKeys);
		if (JSON.stringify(resetValues) === JSON.stringify(currentValues)) return currentValues;
		before = after;
		currentValues = resetValues;
	}
}

/**
 * The evaluation without the children that [edit] removed, with the later children at their new positions, so that
 * each child is compared with its own earlier evaluation.
 */
function withoutRemovedChildren(
	properties: PartConfigurationPropertyEvaluation[],
	edit: PropertyModelEdit,
	path: PropertyModelNodePath,
): PartConfigurationPropertyEvaluation[] {
	const removed = edit.removedChildIndexes ?? [];
	if (removed.length === 0) return properties;
	const step = edit.path[path.length];
	return properties.map((property) => {
		if (property.children == null) return property;
		if (step != null) {
			if (property.name !== step.propertyName) return property;
			return {
				...property,
				children: property.children.map((child) =>
					(child.index ?? 0) === step.index ?
						{
							...child,
							properties: withoutRemovedChildren(child.properties, edit, [...path, step]),
						}
					:	child,
				),
			};
		}
		if (property.name !== edit.propertyNames[0]) return property;
		return {
			...property,
			children: property.children
				.filter((child) => !removed.includes(child.index ?? 0))
				.map((child) =>
					child.index == null ?
						child
					:	{ ...child, index: child.index - removed.filter((index) => index < child.index!).length },
				),
		};
	});
}

/** See [resetInputsWithChangedDefaults]; children are matched by their positions. */
function removeInputsWithChangedDefaults(
	values: PropertyInputValues,
	before: PartConfigurationPropertyEvaluation[],
	after: PartConfigurationPropertyEvaluation[],
	path: PropertyModelNodePath,
	editedKeys: ReadonlySet<string>,
): PropertyInputValues {
	const beforeByName = new Map(before.map((property) => [property.name, property]));
	const afterByName = new Map(after.map((property) => [property.name, property]));
	const result: PropertyInputValues = {};
	for (const [propertyName, value] of Object.entries(values)) {
		const beforeProperty = beforeByName.get(propertyName);
		const afterProperty = afterByName.get(propertyName);
		const resetChild = (
			child: PartConfigurationSubConfigurationValue,
			index: number,
		): PartConfigurationSubConfigurationValue => {
			const childAt = (property: PartConfigurationPropertyEvaluation | undefined) =>
				property?.children?.find((evaluatedChild) => (evaluatedChild.index ?? 0) === index);
			const beforeChild = childAt(beforeProperty);
			const afterChild = childAt(afterProperty);
			if (beforeChild == null || afterChild == null) return child;
			return {
				...child,
				valuesByPropertyName: removeInputsWithChangedDefaults(
					child.valuesByPropertyName,
					beforeChild.properties,
					afterChild.properties,
					[...path, { propertyName, index }],
					editedKeys,
				),
			};
		};

		if (Array.isArray(value)) {
			result[propertyName] = value.map(resetChild);
		} else if (isSubConfigurationInput(value)) {
			result[propertyName] = resetChild(value, 0);
		} else if (
			beforeProperty == null ||
			// A value that a rule fixed was not the user's choice, so lifting the rule keeps it as a given value.
			beforeProperty.valueOrigin === "FIXED" ||
			afterProperty?.defaultValue === undefined ||
			editedKeys.has(`${propertyModelNodePathKey(path)}#${propertyName}`) ||
			JSON.stringify(defaultOf(beforeProperty)) === JSON.stringify(afterProperty.defaultValue)
		) {
			result[propertyName] = value;
		}
	}
	return result;
}

/** The value that an input's default gives: reported for a given value, or the value itself when it came from it. */
function defaultOf(property: PartConfigurationPropertyEvaluation): PartConfigurationValue | undefined {
	if (property.defaultValue !== undefined) return property.defaultValue;
	return property.valueOrigin === "DEFAULT" ? property.value : undefined;
}

/** See [evaluatePropertyModelInput]; children are matched by their positions. */
export function removeInputsWithoutField(
	values: PropertyInputValues,
	properties: PartConfigurationPropertyEvaluation[],
): PropertyInputValues {
	const evaluationsByName = new Map(properties.map((property) => [property.name, property]));
	const result: PropertyInputValues = {};
	for (const [propertyName, value] of Object.entries(values)) {
		const property = evaluationsByName.get(propertyName);
		if (property == null || !property.applicable) continue;

		const pruneChild = (
			child: PartConfigurationSubConfigurationValue,
			index: number,
		): PartConfigurationSubConfigurationValue => {
			const evaluated = property.children?.find((evaluatedChild) => (evaluatedChild.index ?? 0) === index);
			if (evaluated == null) return child;
			return {
				...child,
				valuesByPropertyName: removeInputsWithoutField(child.valuesByPropertyName, evaluated.properties),
			};
		};

		if (Array.isArray(value)) {
			result[propertyName] = value.map(pruneChild);
		} else if (isSubConfigurationInput(value)) {
			result[propertyName] = pruneChild(value, 0);
		} else if (!property.fixed || JSON.stringify(value) === JSON.stringify(property.value)) {
			result[propertyName] = value;
		}
	}
	return result;
}

/**
 * Positions of the children given for [propertyName] that the evaluation left out, such as children of component
 * versions. They have no configuration in the tree, so the form offers removing them.
 */
export function getUnevaluatedChildIndexes(
	values: PropertyInputValues,
	properties: PartConfigurationPropertyEvaluation[],
	propertyName: string,
): number[] {
	const property = properties.find((evaluated) => evaluated.name === propertyName);
	const evaluatedIndexes = new Set((property?.children ?? []).map((child) => child.index ?? 0));
	return getChildInputs(values[propertyName])
		.map((_, index) => index)
		.filter((index) => !evaluatedIndexes.has(index));
}

export function isSubConfigurationProperty(property: PartConfigurationSchemaProperty): boolean {
	return property.datatype === "SUB_CONFIGURATION" || property.datatype === "SUB_CONFIGURATION_LIST";
}

/**
 * The sub-configuration properties of [node] to which a child can be added, in layout order: applicable inputs whose
 * child part has a property-model version, which a new child gets, and that are lists or have no child yet.
 */
export function getAddableSubConfigurationProperties(
	schemaProperties: PartConfigurationSchemaProperty[],
	node: PropertyModelNode,
	nodeInputValues: PropertyInputValues,
): PartConfigurationSchemaProperty[] {
	const applicableNames = new Set(
		node.properties.filter((property) => property.applicable).map((property) => property.name),
	);
	return schemaProperties.filter(
		(property) =>
			property.kind === "INPUT" &&
			isSubConfigurationProperty(property) &&
			applicableNames.has(property.name) &&
			property.childModelKey != null &&
			(property.datatype === "SUB_CONFIGURATION_LIST" ||
				!isSubConfigurationInput(nodeInputValues[property.name])),
	);
}

/** Whether a property-model tree has children or can get them, as the component form shows its tree only then. */
export function propertyModelTreeHasSubConfigurations(
	schema: PartConfigurationSchema,
	evaluation: PartConfigurationEvaluation,
	rootInputValues: PropertyInputValues,
): boolean {
	const root = getRootNode(evaluation);
	return (
		getChildNodes(root).length > 0 ||
		getAddableSubConfigurationProperties(schema.properties, root, rootInputValues).length > 0
	);
}

/**
 * The label of [node] in the tree: the part of the root, or for a child its property's title, its position in a list
 * and its part.
 */
export function getNodeLabel(schema: PartConfigurationSchema, node: PropertyModelNode): string {
	const partNo = getNodePartNo(schema, node);
	const step = node.path[node.path.length - 1];
	if (step == null) return partNo ?? "";

	const parentModelKey = node.parentModelKey;
	const parentProperties =
		parentModelKey == null ? schema.properties : schema.childModels[parentModelKey]?.properties;
	const property = parentProperties?.find((candidate) => candidate.name === step.propertyName);
	const title = property?.title || step.propertyName;
	const position = property?.datatype === "SUB_CONFIGURATION_LIST" ? ` ${step.index + 1}` : "";
	return `${title}${position}${partNo == null ? "" : `: ${partNo}`}`;
}

export interface PropertyModelNodeIssues {
	node: PropertyModelNode;
	label: string;
	issues: PartConfigurationSchemaValidationIssue[];
}

/** The issues of the tree grouped by configuration, in tree order, with each configuration's tree label. */
export function groupIssuesByNode(
	schema: PartConfigurationSchema,
	evaluation: PartConfigurationEvaluation,
): PropertyModelNodeIssues[] {
	const groups: PropertyModelNodeIssues[] = [];
	const visit = (node: PropertyModelNode) => {
		const issues = getNodeIssues(evaluation, node.path);
		if (issues.length > 0) groups.push({ node, label: getNodeLabel(schema, node), issues });
		getChildNodes(node).forEach(({ nodes }) => nodes.forEach(visit));
	};
	visit(getRootNode(evaluation));
	return groups;
}
